import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MessageCircle, Check, AlertTriangle, SkipForward, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Guided one-tap-per-person WhatsApp send queue for a month's payslips.
// Browsers block opening many chats at once, so we open one chat at a time
// (pre-filled message + secure payslip link), mark it done, and advance.
export const BulkWhatsAppPayslips = ({ open, onClose, records = [], year, month, monthLabel }) => {
  const [dept, setDept] = useState('all');
  const [contacts, setContacts] = useState({});
  const [sent, setSent] = useState({});      // employee_id -> true
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSent({});
    setDept('all');
    axios.get(`${API_URL}/api/employees/contacts`)
      .then(r => setContacts(Object.fromEntries((r.data || []).map(c => [c.id, c]))))
      .catch(() => {});
  }, [open]);

  const depts = useMemo(
    () => Array.from(new Set(records.map(r => r.department || 'Unknown'))).sort(),
    [records]
  );

  const queue = useMemo(
    () => records.filter(r => dept === 'all' || (r.department || 'Unknown') === dept),
    [records, dept]
  );

  const numFor = (empId) => {
    const c = contacts[empId] || {};
    return (c.whatsapp_no || c.phone || '').replace(/\D/g, '');
  };

  const sendOne = async (record) => {
    setBusyId(record.employee_id);
    try {
      const r = await axios.post(
        `${API_URL}/api/downloads/payslip/${record.employee_id}/share-link?year=${year}&month=${month}`
      );
      const { token, mobile, name, period, net } = r.data || {};
      const url = `${API_URL}/api/downloads/public/payslip/${token}?format=pdf`;
      const msg = `Hi ${name || record.employee_name || ''}, here is your payslip for ${period || monthLabel}.\n`
        + `Net Pay: AED ${Number(net ?? record.net_salary ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.\n`
        + `View / download: ${url}`;
      const digits = String(mobile || '').replace(/\D/g, '') || numFor(record.employee_id);
      window.open(
        digits ? `https://wa.me/${digits}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`,
        '_blank'
      );
      setSent(s => ({ ...s, [record.employee_id]: true }));
    } catch {
      toast({ title: 'Could not create WhatsApp link', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const sentCount = queue.filter(r => sent[r.employee_id]).length;
  const withNumber = queue.filter(r => numFor(r.employee_id)).length;
  const noNumber = queue.length - withNumber;
  const nextUnsent = queue.find(r => !sent[r.employee_id] && numFor(r.employee_id));
  const pct = queue.length ? Math.round((sentCount / queue.length) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-whatsapp-payslips-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-600" />
            Bulk WhatsApp Payslips — {monthLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Department</label>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger data-testid="bulk-wa-dept-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">All departments ({records.length})</SelectItem>
                {depts.map(d => (
                  <SelectItem key={d} value={d}>
                    {d} ({records.filter(r => (r.department || 'Unknown') === d).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span data-testid="bulk-wa-progress-text">{sentCount} of {queue.length} sent</span>
              {noNumber > 0 && (
                <span className="text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {noNumber} without number
                </span>
              )}
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="bg-green-500 rounded-full h-2 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>

          {/* Big action button */}
          <Button
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            disabled={!nextUnsent || busyId !== null}
            onClick={() => nextUnsent && sendOne(nextUnsent)}
            data-testid="bulk-wa-open-next-btn"
          >
            {busyId ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-2" />}
            {nextUnsent
              ? <>Open WhatsApp &amp; Next — {nextUnsent.employee_name}</>
              : sentCount > 0 ? 'All done 🎉' : 'Nothing to send'}
          </Button>
          <p className="text-[11px] text-gray-400 text-center -mt-2">
            Opens one chat at a time with the payslip link pre-filled. Attach the PDF if you also want to send the file.
          </p>

          {/* Per-person list */}
          <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
            {queue.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">No payslips for this selection.</div>
            ) : queue.map(r => {
              const hasNum = !!numFor(r.employee_id);
              const done = !!sent[r.employee_id];
              return (
                <div key={r.employee_id} className="flex items-center gap-3 px-3 py-2" data-testid={`bulk-wa-row-${r.employee_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{r.employee_name}</p>
                    <p className="text-[11px] text-gray-400 truncate">
                      {r.employee_code} · {r.department || 'Unknown'} · AED {Number(r.net_salary || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  {done ? (
                    <span className="text-green-600 text-xs flex items-center gap-1 flex-shrink-0"><Check className="h-4 w-4" /> Sent</span>
                  ) : !hasNum ? (
                    <span className="text-amber-600 text-[11px] flex items-center gap-1 flex-shrink-0"><AlertTriangle className="h-3.5 w-3.5" /> No number</span>
                  ) : (
                    <Button
                      size="sm" variant="outline"
                      className="text-green-700 border-green-200 hover:bg-green-50 flex-shrink-0"
                      disabled={busyId !== null}
                      onClick={() => sendOne(r)}
                      data-testid={`bulk-wa-send-${r.employee_id}`}
                    >
                      {busyId === r.employee_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center">
            <span className="text-[11px] text-gray-400">Secure links valid 30 days</span>
            <Button variant="outline" onClick={onClose} data-testid="bulk-wa-close-btn">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkWhatsAppPayslips;
